import {assertNever} from '@Utils/assert-never.util';
import {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';

export interface PublishingCopy {
  title: string;
  loadingText: string;
  completedText: string;
  ctaText: string;
}

/**
 * Single source of the per-option publish wording, shared by the in-chat loading modal, the dismissal growl
 * and the desktop pending page. Kept out of the modal library so the pending page can read copy without
 * pulling in the modal machinery.
 */
export const getPublishingCopy = (option: PublishingOption): PublishingCopy => {
  switch (option) {
    case PublishingOption.PUBLISH_TO_SOCIAL_MEDIA:
      return {
        title: window.i18next.t('pmwjs_post_social_media'),
        loadingText: window.i18next.t('pmwjs_preparing_social_post'),
        completedText: window.i18next.t('pmwjs_ready_to_post'),
        ctaText: window.i18next.t('pmwjs_open_social_media_post'),
      };
    case PublishingOption.PROMOTE_AS_EVENT:
      return {
        title: window.i18next.t('pmwjs_promote_as_event'),
        loadingText: window.i18next.t('pmwjs_preparing_event'),
        completedText: window.i18next.t('pmwjs_event_ready'),
        ctaText: window.i18next.t('pmwjs_open_event'),
      };
    case PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN:
      return {
        title: window.i18next.t('pmwjs_publish_email_campaign'),
        loadingText: window.i18next.t('pmwjs_preparing_email'),
        completedText: window.i18next.t('pmwjs_email_ready'),
        ctaText: window.i18next.t('pmwjs_open_email_campaign'),
      };
    default:
      return assertNever(option);
  }
};

/**
 * The short label the option carries in the publish menu, reused as the heading on the desktop pending page.
 */
export const getPublishingOptionLabel = (option: PublishingOption): string => {
  switch (option) {
    case PublishingOption.PUBLISH_TO_SOCIAL_MEDIA:
      return window.i18next.t('pmwjs_post_on_social_media');
    case PublishingOption.PROMOTE_AS_EVENT:
      return window.i18next.t('pmwjs_turn_into_event_page');
    case PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN:
      return window.i18next.t('pmwjs_send_as_email');
    default:
      return assertNever(option);
  }
};

/** The growls show the same CTA as the loading modal, so both read it from the one copy table above. */
export const getPublishingCtaText = (option: PublishingOption): string => {
  return getPublishingCopy(option).ctaText;
};
